import React, { lazy, Suspense } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, Route, Routes } from 'react-router-dom';
import HomeSkeleton from '../components/common/Skeletons/HomeSkeleton';
import DataTableSkeleton from '../components/common/Skeletons/DataTableSkeleton';
import ReportSection from '../components/dashboard/Reports/ReportsSection';
const Assets = lazy(() => import('../components/dashboard/Reports/Asset'));
const Home = lazy(() => import('../components/dashboard/Dashboard/Home'));
const AppAccessReport = lazy(() => import('../components/dashboard/Reports/AppAccessReport'));
const AssetAccessReport = lazy(() => import('../components/dashboard/Reports/AssetAccessReport'));
const WebAppAccessReport = lazy(() => import('../components/dashboard/Reports/WebAppAccessReport'));
const AppsReport = lazy(() => import('../components/dashboard/Reports/AppsReport'));
const Users = lazy(() => import('../components/dashboard/Reports/User'));
const AppAllocationReport = lazy(() => import('../components/dashboard/Reports/AppAllocationReport'));
const AssetAllocationReport = lazy(() => import('../components/dashboard/Reports/AssetAllocationReport'));
const WebAppAllocationReport = lazy(() => import('../components/dashboard/Reports/WebAppsAllocationReport'));
const ApplicationCredentialsAllocationReport = lazy(() => import('../components/dashboard/Reports/ApplicationCredentialsAllocationReport'));
const TicketsReport = lazy(() => import('../components/dashboard/Reports/TicketReports'));
const AppAccessDetails = lazy(() => import('../components/dashboard/Reports/AppAccessDetails/AppAccessDetails'));
const AssetAccessDetails = lazy(() => import('../components/dashboard/Reports/AssetAccessDetails/AssetAccessDetails'));
const Activeusers = lazy(() => import('../components/dashboard/LiveSession/ActiveSessions'));
const Recordings = lazy(() => import('../components/dashboard/Recordings/Recordings'));
const UsersProfile = lazy(() => import('../components/dashboard/UsersProfile/UserProfile'));
const ChangePassword = lazy(() => import('../components/dashboard/AdminPassword/ChangePassword'));
const WebApps = lazy(() => import('../components/dashboard/Reports/WebApps'));
const PasswordRotationPolicyReport = lazy(() => import('../components/dashboard/Reports/PasswordRotationPolicy'));
const SystemUserPasswordRotationReport = lazy(() =>
  import('../components/dashboard/Reports/SystemUserPasswordRotation')
);
const ExportReports = lazy(() => import('../components/common/CustomModal/ExportReports.js'));
const UsersReport = lazy(() => import('../components/dashboard/Reports/UsersReport'));
const SystemUserReport = lazy(() => import('../components/dashboard/Reports/SystemUserReport'));
const RolesReport = lazy(() => import('../components/dashboard/Reports/RolesReport'));
const GroupsReport = lazy(() => import('../components/dashboard/Reports/GroupsReport'));
const RdpAccessReport = lazy(() => import('../components/dashboard/Reports/RdpAccessReport'));
const RdpHandlerDiffReport = lazy(() => import('../components/dashboard/Reports/RdpHandlerDiffReport'));
const ClientCredentialsReport = lazy(() => import('../components/dashboard/Reports/ClientCredentialsReport'));
const ClientCredentialsUsageReport = lazy(() => import('../components/dashboard/Reports/ClientCredentialsUsageReport'));
const CertificateReport = lazy(() => import('../components/dashboard/Reports/CertificateReport'));
const CredentialActionReport = lazy(() => import('../components/dashboard/Reports/CredentialActionReport'));
const ApplicationCredentialAccessReport = lazy(() => import('../components/dashboard/Reports/ApplicationCredentialAccessReport'));
const CloudDiscoveryConfigReport = lazy(() => import('../components/dashboard/Reports/CloudDiscoveryConfigReport'));
const CloudDiscoveryScanReport = lazy(() => import('../components/dashboard/Reports/CloudDiscoveryScanReport'));
const CloudDiscoveryOnboardReport = lazy(() => import('../components/dashboard/Reports/CloudDiscoveryOnboardReport'));
const CloudDiscoveryOnboardDetails = lazy(() => import('../components/dashboard/Reports/CloudDiscoveryOnboardDetails/CloudDiscoveryOnboardDetails'));

const AuditDashboardRoutes = () => {
  const capabilities = useSelector((state) => state.user?.user?.role?.capabilities || {});
  const isCapable = 'is_admin_user' in capabilities ? capabilities : false;
  const isCapableEnduser = 'is_end_user' in capabilities ? capabilities : false;
  const { isAuthenticated } = useSelector((state) => state.user);

  return (
    <Routes>
      <Route
        path=""
        element={
          <Suspense fallback={<HomeSkeleton />}>
            <Home isAuditDashboard={true} />
          </Suspense>
        }
      />
      {isAuthenticated && <Route path="*" element={<Navigate to="/audit-dashboard" />} />}
      {isCapable && isCapable.report.show_reports && <Route path="reports" element={
        <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
          <ReportSection />
        </Suspense>
      } />}
      {isCapable && isCapable.report.assets_report && <Route path="reports/servers-report" element={
        <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
          <Assets />
        </Suspense>
      } />}
      {isCapable && isCapable.report.schedule_reports && <Route path="schedule_reports" element={
        <Suspense>
          <ExportReports />
        </Suspense>
      } />}
      {isCapable && isCapable.report.web_apps_report && (
        <Route path="reports/web-apps-report" element={
          <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
            <WebApps />
          </Suspense>
        } />
      )}
      {isCapable && isCapable.report.apps_access_report && (
        <Route path="reports/databases-access-report" element={
          <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
            <AppAccessReport />
          </Suspense>
        } />
      )}
      {isCapable && isCapable.report.assets_access_report && (
        <Route path="reports/servers-access-report" element={
          <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
            <AssetAccessReport />
          </Suspense>
        } />
      )}

      {isCapable && isCapable.report.web_apps_access_report && (
        <Route path="reports/web-apps-access-report" element={
          <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
            <WebAppAccessReport />
          </Suspense>
        } />
      )}
      {isCapable && isCapable.report.apps_report && (
        <Route path="reports/databases-report" element={
          <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
            <AppsReport />
          </Suspense>
        } />
      )}
      {/*{isCapable && isCapable.report.active_usage_report && <Route path="active_usage" element={<Sessions />} />}*/}
      {isCapable && isCapable.report.user_login_report && (
        <Route path="reports/users-login-report" element={
          <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
            <Users />
          </Suspense>
        } />
      )}
      {isCapable && isCapable.report.apps_allocation_report && (
        <Route path="reports/databases-allocation-report" element={
          <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
            <AppAllocationReport />
          </Suspense>
        } />
      )}
      {isCapable && isCapable.report.assets_allocation_report && (
        <Route path="reports/servers-allocation-report" element={
          <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
            <AssetAllocationReport />
          </Suspense>
        } />
      )}
      {isCapable && isCapable.report.webapps_allocation_report && (
        <Route path="reports/web-apps-allocation-report" element={
          <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
            <WebAppAllocationReport />
          </Suspense>
        } />
      )}
      {isCapable && isCapable.report.application_credentials_allocation_report && (
        <Route path="reports/application-credentials-allocation-report" element={
          <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
            <ApplicationCredentialsAllocationReport />
          </Suspense>
        } />
      )}
      {isCapable && isCapable.report.ticket_report && (
        <Route path="reports/tickets-report" element={
          <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
            <TicketsReport />
          </Suspense>
        } />
      )}
      {isCapable && isCapable.report.apps_access_report && (
        <Route
          path="reports/databases-access-report/database-access-details/:app_access_id/:app_name/:user_name/:is_recorded"
          element={
            <Suspense>
              <AppAccessDetails />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.report.assets_access_report && (
        <Route
          path="reports/servers-access-report/server-access-details/:asset_access_id/:asset_name/:user_name/:is_recorded"
          element={
            <Suspense>
              <AssetAccessDetails />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.report.assets_access_report && (
        <Route
          path="reports/servers-access-report/rdp-access-details/:access_id/:asset_name/:user_name"
          element={
            <Suspense>
              <RdpAccessReport />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.report.assets_access_report && (
        <Route
          path="reports/servers-access-report/rdp-access-details/rdp-handler-diff-details/:handler_id/:asset_name/:user_name"
          element={
            <Suspense>
              <RdpHandlerDiffReport />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.report.assets_access_report && (
        <Route
          path="reports/rdp-access-report/rdp-access-details/:asset_access_id/:asset_name/:user_name/:is_recorded"
          element={
            <Suspense>
              <AssetAccessDetails />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.report.live_session === true && <Route path="live" element={
        <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
          <Activeusers />
        </Suspense>
      } />}
      {isCapable && isCapable.report.recordings && <Route path="recordings" element={
        <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
          <Recordings />
        </Suspense>
      } />}
      {isCapable && isCapable.report.credentials_checkout_report && (
        <Route path="reports/credential-action-report" element={
          <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
            <CredentialActionReport />
          </Suspense>
        } />
      )}
      {isCapable && isCapable.report.credentials_checkout_report && (
        <Route path="reports/application-credentials-access-report" element={
          <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
            <ApplicationCredentialAccessReport />
          </Suspense>
        } />
      )}
      <Route path="users-profile" element={
        <Suspense>
          <UsersProfile />
        </Suspense>
      } />
      {((isCapableEnduser && isCapableEnduser.allow_password_change) ||
        (isCapable && isCapable.users.user.change_password)) && (
        <Route path="change-password" element={
          <Suspense>
            <ChangePassword />
          </Suspense>
        } />
        )}
      {isCapable && isCapable.report.password_rotation_policy_report && (
        <Route path="reports/passwords-rotation-policy-report" element={
          <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
            <PasswordRotationPolicyReport />
          </Suspense>
        } />
      )}
      {isCapable && isCapable.report.system_user_password_rotation_report && (
        <Route
          path="reports/system-users-password-rotation-policy-report"
          element={
            <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
              <SystemUserPasswordRotationReport />
            </Suspense>
          }
        />
      )}
      {isCapable && isCapable.report.users_report && <Route path="reports/users-report" element={
        <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
          <UsersReport />
        </Suspense>
      } />}
      {isCapable && isCapable.report.systemuser_report && (
        <Route path="reports/system-users-report" element={
          <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
            <SystemUserReport />
          </Suspense>
        } />
      )}
      {isCapable && isCapable.report.roles_report && <Route path="reports/roles-report" element={
        <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
          <RolesReport />
        </Suspense>
      } />}
      {isCapable && isCapable.report.groups_report && (
        <Route path="reports/groups-report" element={
          <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
            <GroupsReport />
          </Suspense>
        } />
      )}
      {isCapable && isCapable.report.client_credential_report && (
        <Route path="reports/client-credentials-report" element={
          <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
            <ClientCredentialsReport />
          </Suspense>
        } />
      )}
      {isCapable && isCapable.report.certificate_report && (
        <Route path="reports/certificate-report" element={
          <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
            <CertificateReport />
          </Suspense>
        } />
      )}
      {isCapable && isCapable.report.client_credential_report && (
        <Route path="reports/client-credentials-usage-report" element={
          <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
            <ClientCredentialsUsageReport />
          </Suspense>
        } />
      )}
      {isCapable && isCapable.report?.cloud_discovery_report && (
        <>
          <Route path="reports/cloud-discovery-config-report" element={
            <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
              <CloudDiscoveryConfigReport />
            </Suspense>
          } />
          <Route path="reports/cloud-discovery-scan-report" element={
            <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
              <CloudDiscoveryScanReport />
            </Suspense>
          } />
          <Route path="reports/cloud-discovery-scan-report/onboarded-resources/:scan_id/:config_name?" element={
            <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
              <CloudDiscoveryOnboardDetails />
            </Suspense>
          } />
          <Route path="reports/cloud-discovery-onboard-report" element={
            <Suspense fallback={<DataTableSkeleton rowCount={7} columnCount={5} />}>
              <CloudDiscoveryOnboardReport />
            </Suspense>
          } />
        </>
      )}
    </Routes>
  );
};

export default AuditDashboardRoutes;
